import React from 'react';
import { Loader2, Send } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { moduleApi } from '../../lib/api';

interface Recipient { type: 'collaborator' | 'client'; id: string; label: string; email: string; }

/** Délègue une demande de booking d'extras à un autre compte (collaborateur ou "Direction" = le compte
 *  client) — sans toucher à la logique de booking elle-même (interne/externe, statuts...), qui reste
 *  entièrement gérée par le bouton "Booker" existant. Le destinataire reçoit un email avec un lien qui le
 *  ramène directement au module Événementiel, sur l'événement (ou le calendrier) concerné. */
export function RequestBookingModal({ title, onSend, onClose }: {
    title: string;
    onSend: (recipient: Recipient) => Promise<{ success?: boolean; error?: string }>;
    onClose: () => void;
}) {
    const [loading, setLoading] = React.useState(true);
    const [recipients, setRecipients] = React.useState<Recipient[]>([]);
    const [selectedId, setSelectedId] = React.useState<string>('');
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState('');
    const [sent, setSent] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getEvenementielBookingRecipients()
            .then((r: any) => setRecipients(Array.isArray(r) ? r : []))
            .catch(() => setRecipients([]))
            .finally(() => setLoading(false));
    }, []);

    const handleSend = async () => {
        const recipient = recipients.find((r) => `${r.type}:${r.id}` === selectedId);
        if (!recipient) return;
        setSending(true);
        setError('');
        try {
            const res = await onSend(recipient);
            if (res?.error) { setError(res.error); return; }
            setSent(true);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal open onClose={onClose} title={title} icon={<Send size={16} />} maxWidth="max-w-md"
            footer={sent ? <Button variant="primary" onClick={onClose}>Fermer</Button> : (
                <>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button variant="primary" onClick={handleSend} disabled={sending || !selectedId}>{sending ? 'Envoi...' : 'Envoyer'}</Button>
                </>
            )}>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : sent ? (
                <p className="text-sm text-content">Demande envoyée.</p>
            ) : recipients.length === 0 ? (
                <p className="text-xs text-content-muted">Aucun autre compte à qui envoyer cette demande.</p>
            ) : (
                <div className="space-y-1.5">
                    <p className="text-xs text-content-muted mb-2">À qui envoyer la demande de booking des extras ?</p>
                    {recipients.map((r) => {
                        const key = `${r.type}:${r.id}`;
                        return (
                            <button key={key} type="button" onClick={() => setSelectedId(key)}
                                className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm transition-all ${selectedId === key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                                <span className="font-bold">{r.label}</span>
                                <span className={`block text-xs ${selectedId === key ? 'text-brand-fg/80' : 'text-content-muted'}`}>{r.email}</span>
                            </button>
                        );
                    })}
                    {error && <p className="text-xs text-red-500 font-semibold mt-2">{error}</p>}
                </div>
            )}
        </Modal>
    );
}
